<template>
  <!-- Page length options -->
  <div class="card">
    <div>
      <div
        v-show="onload"
        class="content bg-white"
      >
        <placeholder-content is-load="true" />
      </div>
      <div v-if="!onload && hasPermission">
        <access-denied />
      </div>
      <div
        v-show="!onload && !hasPermission"
        class="card-header bg-white header-elements-inline"
      >
        <h5 class="card-title">
          {{ $t("Sales Report by Daily and Period") }}
        </h5>
      </div>
      <div
        v-show="!onload && !hasPermission"
        class="card-body"
      >
        <div v-show="onload">
          <div class="bg-white">
            <placeholder-content is-load="true" />
          </div>
        </div>
  
        <div v-show="!onload">
          <div class="d-flex align-items-center justify-content-start border-bottom mb-3 p-3 bg-light mt-n3 mx-n3">
            <span class="nowrap mr-3">{{ $t("Data Duration") }}</span>
            <div class="input-group w-auto">
              <span class="input-group-prepend">
                <span class="input-group-text"><i class="fas fa-calendar-alt" /></span>
              </span>
              <month-picker-input 
                @change="applyDatePicker"
                :no-default="false"
                :show-year="true"
                :default-month="defaultMonth"
                :default-year="defaultYear"
                lang="th"
              />
              <button @click="openAdvanceFilter" type="button" class="btn btn-outline-normal rounded-left-0 d-flex" data-toggle="modal" data-target="#advance_filter">
                  <i class="fas fa-sliders-v mr-2 mt-1"></i>
                  {{$t("More Filters")}}
                  <span v-if="countFilter" class="badge badge-secondary ml-2">{{ countFilter }}</span>
              </button>
            </div>
          </div>
        </div>
  
        <div
          v-if="!onload"
          class="d-flex flex-wrap"
        >
          <div class="card mr-3">
            <div class="card-body">
              <span class="rounded-circle badge badge-icon bg-primary position-absolute top-0 right-0 mt-3 mr-3"><i class="fas fa-sack-dollar" /></span>
              <h6 class="mb-0 font-weight-semibold">
                {{ $t("Total sales") }}
              </h6>
              <h3 class="text-grey-800 font-weight-semibold my-1">
                ฿{{ amountTotalSales.amount | formatNumber }}
              </h3>
              <div
                v-if="amountTotalSales.percent == '-' || amountTotalSales.percent == 0"
                class="text-grey-300"
              >
                <span class="mr-1">0.00%</span> {{ $t('compared to last month') }}
              </div>
              <div
                v-else
                class="text-grey-300"
              >
                <span :class="[amountTotalSales.percent >= 0? 'text-green-custom': 'text-danger', 'mr-1']"><i :class="[amountTotalSales.percent >= 0? 'fa-arrow-up': 'fa-arrow-down', 'fas mr-1']" />{{ amountTotalSales.percent | formatNumber }}% </span> {{ $t('compared to last month') }}
              </div>
            </div>
          </div>
  
          <div class="card mr-3">
            <div class="card-body">
              <span class="rounded-circle badge badge-icon bg-primary position-absolute top-0 right-0 mt-3 mr-3"><i
                style="font-size: 11px;"
                class="fa-solid fa-box"
              /></span>
              <h6 class="mb-0 font-weight-semibold">
                {{ $t("Number of Products") }}
              </h6>
              <h3 class="text-grey-800 font-weight-semibold my-1">
                {{ totalProduct.amount.toFixed(0) | formatNumberDigit }}
              </h3>
              <div
                v-if="totalProduct.percent == '-' || totalProduct.percent == 0"
                class="text-grey-300"
              >
                <span class="mr-1">0.00%</span> {{ $t('compared to last month') }}
              </div>
              <div
                v-else
                class="text-grey-300"
              >
                <span :class="[totalProduct.percent >= 0? 'text-green-custom': 'text-danger', 'mr-1']"><i :class="[totalProduct.percent >= 0? 'fa-arrow-up': 'fa-arrow-down', 'fas mr-1']" />{{ totalProduct.percent | formatNumber }}% </span> {{ $t('compared to last month') }}
              </div>
            </div>
          </div>
  
          <div class="card mr-3">
            <div class="card-body">
              <span class="rounded-circle badge badge-icon bg-primary position-absolute top-0 right-0 mt-3 mr-3"><i class="fas fa-shopping-cart" /></span>
              <h6 class="mb-0 font-weight-semibold">
                {{ $t("_Shipping.Number of Orders") }}
              </h6>
              <h3 class="text-grey-800 font-weight-semibold my-1">
                {{ totalOrder.amount.toFixed(0) | formatNumberDigit }}
              </h3>
              <div
                v-if="totalOrder.percent == '-' || totalOrder.percent == 0"
                class="text-grey-300"
              >
                <span class="mr-1">0.00%</span> {{ $t('compared to last month') }}
              </div>
              <div
                v-else
                class="text-grey-300"
              >
                <span :class="[totalOrder.percent >= 0? 'text-green-custom': 'text-danger', 'mr-1']"><i :class="[totalOrder.percent >= 0? 'fa-arrow-up': 'fa-arrow-down', 'fas mr-1']" />{{ totalOrder.percent | formatNumber }}% </span> {{ $t('compared to last month') }}
              </div>
            </div>
          </div>
        </div>
  
  
        <div class="row">
          <div class="col-12 col-xl-6">
            <SaledailyBarChart
              v-if="!onload"
              :chart-columns="chartColumns"
              :y-axis-text="yAxisText"
            />
          </div>
  
          <div class="col-12 col-xl-6">
            <SaledailyLineChart
              v-if="!onload"
              :chart-line-columns="lineChartColumns"
              :y-axis-text="yAxisText"
            />
          </div>
        </div>
  
        <div
          class="card"
          v-show="!onload"
        >
          <div
            :class="['table-responsive mt-0 data-nowrap table-order']"
            id="table-order"
          >
            <div class="vuetable-wrapper datatable-scroll">
              <vuetable
                :class="[loadingTable]"
                class="overflow-x-auto"
                ref="vuetable"
                :no-data-template="$t('No Data Available')"
                :fields="table.fields"
                data-path="data"
                pagination-path=""
                :api-mode="false"
                :data-manager="dataManager"
                @vuetable:loaded="vuetableLoaded"
                @vuetable:loading="vuetableLoading"
                @vuetable:pagination-data="onPaginationData"
                :sort-order="sort"
              >
                <template
                  slot="name"
                  slot-scope="props"
                >
                  <router-link 
                    :to="{
                      name: 'admin.reports.orders.dailyDetail', 
                      params: { 
                        dayCode: props.rowData.name, 
                        month: paramsQuery.month, 
                        year: paramsQuery.year, 
                      }
                    }" 
                    target="_blank"
                  >
                    {{ $t("_reports."+props.rowData.name) }}
                  </router-link>
                </template>
              </vuetable>
            </div>
            <div class="datatable-footer">
              <vuetable-pagination-info
                class="dataTables_info ml-3"
                ref="paginationInfo"
                :no-data-template="replaceStr"
                :info-template="$t('Displaying {from} to {to} of {total} items')"
              />
              <div class="dataTables_paginate paging_simple_numbers mb-0 mr-2">
                <vuetable-pagination
                  ref="pagination"
                  pagination-path="pagination"
                  @vuetable-pagination:change-page="onChangePage"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="modal fade"
        tabindex="-1"
        role="dialog"
        id="exampleModalLabel"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div
          class="modal-dialog"
          role="document"
        >
          <div class="modal-content">
            <div class="modal-header">
              <button
                type="button"
                class="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <img
                :src="showbigimage"
                class="showbigimage"
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <advanceFilterSaleDaily 
      v-if="isadvanceFilter"
      @close="isadvanceFilter=false"
      :statusList="statusList"
      :filter="filter"
      @payment_method="getPaymentMethod"
    />
  </div>
</template>
  
  <script>
  import Api from "@/services/endpoint/tenant.service";
  import DateRangePicker from "vue2-daterange-picker";
  import PerPage from "../PerPage.vue";
  import Select2 from "v-select2-component";
  import Vuetable from "vuetable-2";
  import VuetablePagination from "vuetable-2/src/components/VuetablePagination";
  import VuetablePaginationInfo from "@/plugins/vuetable-2/VuetablePaginationInfo";
  import fields from "../assets/js/salesdaily_fields.js";
  import {
      showTextPicker,
  } from "../assets/directives/Vuetable.js";
  import moment from "moment";
  import {
      convertTimezone
  } from "@/common/tenancy/convertTimezone";
  import numeral from "numeral";
  import PlaceholderContent from "@/plugins/PlaceholderContent.vue";
  import SaledailyBarChart from "./saledaily/SaledailyBarChart.vue";
  import SaledailyLineChart from "./saledaily/SaledailyLineChart.vue";
  import AccessDenied from "@/components/tenancy/admin/error-page/templates/AccessDenied.vue";
  import { MonthPicker } from 'vue-month-picker'
  import { MonthPickerInput } from 'vue-month-picker'
  import advanceFilterSaleDaily from "./saledaily/AdvanceFilterSaleDaily";
  export default {
      components: {
          DateRangePicker,
          PerPage,
          Select2,
          Vuetable,
          VuetablePagination,
          VuetablePaginationInfo,
          PlaceholderContent,
          SaledailyBarChart,
          SaledailyLineChart,
          AccessDenied,
          MonthPicker,
          MonthPickerInput,
          advanceFilterSaleDaily
      },
      data() {
          return {
              checkedCount: 0,
              defaultStartDate: moment().subtract(1, 'months').format('YYYY-MM-DD 17:00:00'),
              defaultEndDate: moment().format('YYYY-MM-DD 16:59:59'),
              defaultMonth: moment().format('MM')*1,
              defaultYear: moment().format('YYYY')*1,
              monthName: null,
              isadvanceFilter: false,
              arrMonth: {
                  1:"January", 
                  2:"February", 
                  3:"March", 
                  4:"April", 
                  5:"May", 
                  6:"June",
                  7:"July", 
                  8:"August", 
                  9:"September", 
                  10:"October", 
                  11:"November", 
                  12:"December",
              },
              table: {
                  fields: fields,
              },
              showbigimage: '',
              paramsQuery: {
                  // start_date: this.defaultStartDate,
                  // end_date: this.defaultEndDate,
                  month: 0,
                  year: 0,
                  status: [],
                  payment_method: [],
                  payment_status: [],
                  productTypeId: 'all',
                  perspectiveId: 'sum_order_quantity',
                  per_page: 10,
                  current_page: 1,
                  sort: '',
                  page: 1
              },
              dateRange: {
                  startDate: this.defaultStartDate,
                  endDate: this.defaultEndDate,
              },
              sort: [{
                  field: 'sum_order_quantity',
                  direction: 'desc'
              }],
              productType: [],
              perspective: [{
                  id: 'sum_order_quantity',
                  field: 'sum_order_quantity',
                  direction: 'desc',
                  sortField: 'sum_order_quantity',
                  text: this.$t('_Shipping.Number of Orders')
              },{
                  id: 'sum_product_order_quantity',
                  field: 'sum_product_order_quantity',
                  direction: 'desc',
                  sortField: 'sum_product_order_quantity',
                  text: this.$t('Number of Products')
              },{
                  id: 'sum_product_total',
                  field: 'sum_product_total',
                  direction: 'desc',
                  sortField: 'sum_product_total',
                  text: this.$t('Sales')
              }],
              ProductTypeSetting: {
                  minimumResultsForSearch: Infinity,
                  placeholder: this.$t("Product Type") + "..",
              },
              PerspectiveSetting: {
                  minimumResultsForSearch: Infinity,
                  placeholder: this.$t("perspective") + "..",
              },
              loadProductType: false,
              loadPerspective: false,
              onload: true,
              hasPermission: true,
              ranges: this.$storeIndex.ranges(),
              localedata: {
                  format: 'DD-MM-YYYY',
                  applyLabel: this.$t('Apply'),
                  cancelLabel: this.$t('Cancel'),
                  clearable: true,
                  singleDatePicker: true,
                  showDropdowns: true,
                  monthNames: this.$storeIndex.monthNames(),
                  daysOfWeek: this.$storeIndex.daysOfWeek(),
              },
              paymentMethodList: [],
              channelsList: [],
              countFilter: 0,
              filter: {
                  status: [],
                  payment_method: [],
                  payment_status: [],
                  channels: [],
                  dateRange: {
                    startDate: null,
                    endDate: null,
                  },
                  month: null,
                  year: null,
                  saleOrderID: null,
                  tag: [],
              },
              statusList: [
                  {
                    id: "pending",
                    icon: '<i class="fa-solid fa-clock"></i>',
                    name: "_Shipment.Pending",
                  },
                  {
                    id: "processing",
                    icon: '<i class="fas fa-sync-alt"></i>',
                    name: "Processing",
                  },
                  {
                    id: "completed",
                    icon: '<i class="fas fa-check-circle"></i>',
                    name: "Completed",
                  },
                  {
                    id: "returned",
                    icon: '<i class="fa-solid fa-arrow-rotate-left"></i>',
                    name: "_Order.Returned",
                  },
                  {
                    id: "canceled",
                    icon: '<i class="fas fa-times-square"></i>',
                    name: "Cancel",
                  },
              ],
              paymentStatusList: [
                  {
                    id: "0",
                    name: "Waiting",
                  },
                  {
                    id: "1",
                    name: "Partial",
                  },
                  {
                    id: "2",
                    name: "Paid",
                  },
              ],
              dateTxtFilter: null,
              loadingTable: null,
              i: 0,
              chartColumns: {},
              lineChartColumns: {},
              amountTotalSales: {
                  amount: 0,
                  percent: 0
              },
              totalOrder: {
                  amount: 0,
                  percent: 0
              },
              totalProduct: {
                  amount: 0,
                  percent: 0
              },
              yAxisText: this.$t('_Shipping.Number of Orders'),
          };
      },
      created() {
        this.filter.status = ["pending", "processing", "completed"];
        this.paramsQuery.status = ["pending", "processing", "completed"];
        if(this.paramsQuery.status.length > 0){
          this.countFilter += 1;
        }
      },
      computed: {
          replaceStr: function() {
            var txtDisplay = this.$t('Displaying {from} to {to} of {total} items');
            txtDisplay = txtDisplay.replace('{from}', '1').replace('{to}', '7').replace('{total}', '7');
            return txtDisplay;
          },
          showTextPicker: function () {
              if (this.dateRange.startDate && this.dateRange.endDate) {
                  this.dateRange.startDate = convertTimezone.utcToLocal(this.dateRange.startDate);
                  this.dateRange.endDate = convertTimezone.utcToLocal(this.dateRange.endDate);
                  this.dateTxtFilter = showTextPicker(this.dateRange);
                  return this.dateTxtFilter;
              } else {
                  return showTextPicker(this.paramsQuery);
              }
          },
      },
      methods: {
          dataManager(sortOrder, pagination) {
              if (sortOrder.length > 0) {
                  this.paramsQuery.sort = sortOrder.map((item) => `${item.sortField}|${item.direction}`).join(',')
              }
              if (this.i++ > 0) {
                  this.setParams();
              }
          },
          onPaginationData(tablePagination) {
              this.$refs.paginationInfo.setPaginationData(tablePagination);
              this.$refs.pagination.setPaginationData(tablePagination);
          },
          onChangePage(page) {
              this.$refs.vuetable.changePage(page);
          },
          search() {
              this.setParams();
          },
          changeProductTypeEvent(val) {
              this.setParams(1);
          },
          changePerspectiveEvent(val) {
              let perspective = this.perspective.filter((item) => item.id == val);
              this.yAxisText = perspective[0].text;
              this.sort = perspective;
              this.paramsQuery.sort = perspective.map((item) => `${item.sortField}|${item.direction}`).join(',');
              this.setParams(1);
          },
          setPerPage(perPage) {
              this.paramsQuery.current_page = 1;
              this.$refs.vuetable.currentPage = 1;
              this.paramsQuery.per_page = perPage;
              this.setParams();
          },
          vuetableLoaded() {
              // this.onload = false;
          },
          vuetableLoading() {},
          cancelDateRange() {
              this.paramsQuery.start_date = this.defaultStartDate;
              this.paramsQuery.end_date = this.defaultEndDate;
              $(".daterangepicker").remove();
              this.setParams();
          },
          applyDatePicker(date) {
              this.paramsQuery.month = date.monthIndex;
              this.monthName = this.arrMonth[date.monthIndex];
              this.paramsQuery.year = date.year;
              this.setParams(1);
          },
          showimage(data) {
              this.showbigimage = data;
          },
          openAdvanceFilter() {
            this.isadvanceFilter = true;
            this.$nextTick(() => {
              this.isadvanceFilter = true;
            });
          },
          getPaymentMethod(data) {
            this.paymentMethodList = data;
          },
          filterinfo(data) {
            this.filter = data;
            this.update_filter();
          },
          update_filter() {
            let status = this.filter.status.length;
            let payment_status = this.filter.payment_status.length;
            let payment_method = this.filter.payment_method.length;
            let channels = this.filter.channels.length;
            let tag = this.filter.tag.length;
            if (status || payment_status || payment_method || channels || tag) {
              this.isFilter = true;
            } else {
              this.isFilter = false;
            }
            this.paramsQuery.status = this.filter.status;
            this.paramsQuery.payment_status = this.filter.payment_status;
            this.paramsQuery.payment_method = this.filter.payment_method;
            this.paramsQuery.tag = this.filter.tag;
            if (this.filter.dateRange.startDate) {
              this.paramsQuery.start_date = moment(
                this.filter.dateRange.startDate
              ).format("YYYY-MM-DD");
            }
            if (this.filter.dateRange.endDate) {
              this.paramsQuery.end_date = moment(
                this.filter.dateRange.endDate
              ).format("YYYY-MM-DD");
            }
            this.paramsQuery.channels = this.filter.channels;
      
            // this.countFilter =
            //   status + payment_method + payment_status + channels + tag; // count morefilter
      
            this.$nextTick(() => {
              this.$refs.vuetable.refresh();
            });
          },
          listFilter(type, data) {
            if (type == "status") {
              let index = this.statusList.findIndex((p) => p.id == data);
              return this.statusList[index].name;
            } else if (type == "payment_method") {
              let index = this.paymentMethodList.findIndex((p) => p.id == data);
              return this.paymentMethodList[index].name;
            } else if (type == "payment_status") {
              let index = this.paymentStatusList.findIndex((p) => p.id == data);
              return this.paymentStatusList[index].name;
            } else if (type == "channels") {
              let index = this.channelsList.findIndex((p) => p.id == data);
              return this.channelsList[index].name;
            }
          },
          delFilter(type, data) {
            if (type == "status") {
              this.filter.status = [];
            } else if (type == "payment_method") {
              let index = this.filter.payment_method.indexOf(data);
              this.filter.payment_method.splice(index, 1);
            } else if (type == "payment_status") {
              let index = this.filter.payment_status.indexOf(data);
              this.filter.payment_status.splice(index, 1);
            } else if (type == "channels") {
              let index = this.filter.channels.indexOf(data);
              this.filter.channels.splice(index, 1);
            }
          },
          async setParams(page = null) {
            this.mapPaymentMethod()
              try {
                  if (this.i++ > 0) {
                      this.loadingTable = "loading";
                      this.onload = true;
                  }
                  let queryString = "";
                  if (this.paramsQuery) {
                      queryString += "?" + $.param(this.paramsQuery);
                  }
                  this.setQuerystring(this.paramsQuery);
  
                  //set chart
                  let dataResult = await Api.Report.getSalesOrderDailyPayments(queryString);

                  //set chart
                  this.chartColumns = dataResult.data.chart.daily;
                  this.lineChartColumns = dataResult.data.chart.time;
  
                  //set card
                  this.amountTotalSales = dataResult.data.card.totalsale;
                  this.totalProduct = dataResult.data.card.products;
                  this.totalOrder = dataResult.data.card.orders;
  
                  //set datatable
                  this.$refs.vuetable.setData(this.chartColumns);
  
                  this.loadingTable = "";
                  this.hasPermission = false;
                  this.onload = false;
              }
              catch (error) {
                  if (error.response.status == 403) {
                      this.onload = false;
                      this.hasPermission = true;
                  } else {
                      this.$router.push({name: "t.a.home"});
                      console.log(error.response, 'error');
                  }
              }
          },
          mapPaymentMethod () {
             if (this.paramsQuery.payment_method || this.paramsQuery.payment_method != undefined) {
              let mapData = [this.paramsQuery.payment_method]
              // mapData = Array.from(mapData.toString())
              this.paramsQuery.payment_method = mapData.map(value => {
              if (typeof value === 'string') {
                return value.split(',').map(subValue => {
                  return parseInt(subValue);
                });
               }
               return value;
               });
               this.paramsQuery.payment_method = [].concat(...this.paramsQuery.payment_method);
               this.paramsQuery.payment_method = this.paramsQuery.payment_method.filter(el => !isNaN(el));
             }
           },
          setQuerystring(obj) {
              this.$router.push({
                  query: Object.assign({}, this.$route.query, obj)
              }).catch(() => {});
          },
      },
      async mounted() {
          this.monthName = this.arrMonth[this.defaultMonth];
          this.paramsQuery.month = this.defaultMonth;
          this.paramsQuery.year = this.defaultYear;
          this.dateRange.startDate = this.paramsQuery.start_date = this.$route.query.start_date ? this.$route.query.start_date : this.defaultStartDate;
          this.dateRange.endDate = this.paramsQuery.end_date = this.$route.query.end_date ? this.$route.query.end_date : this.defaultEndDate;
          this.onload = true;
          let i = 0;
          let publishProductType = ['Simple', 'Config'];
          const productType = await Api.ProductType.getProductType();
          this.productType[i] = {
              text: this.$t('All'),
              id: 'all',
          };
          await productType.data.data.forEach((data, key) => {
              if (publishProductType.includes(data.name)) {
                  this.productType[++i] = {
                      text: this.$t(data.name + " Product"),
                      id: data.id,
                  };
              }
          });
          this.loadProductType = true;
          this.loadPerspective = true;
          this.setParams();
      },
      filters: {
          formatNumber: function (value) {
              return numeral(value).format("0,0.00");
          },
          formatNumberDigit: function (value){
            return numeral(value).format("0,0");
          },
      },
  };
  </script>
  
  <style scoped>
  .input-group>.input-group-prepend:not(:first-child)>.input-group-text {
      border-top-left-radius: 0.1875rem;
      border-bottom-left-radius: 0.1875rem;
  }
  </style>
  <style>
    input.month-picker-input{
      font-size: 13px !important;
      padding: 7px 7px 7px 15px !important;
      border-radius: 0px !important;
      border-right: 0px !important;
    }
    .month-picker-input-container{
      width: auto !important;
    }
    .month-picker__container{
        z-index: 100;
        width: 300px !important;
    }
  </style>


// WEBPACK FOOTER //
// src/components/tenancy/admin/report/SalesOrder/SalesDaily.vue